import React, { useCallback } from 'react';
import { IconName } from '@frontend/icons';
import { contextFactory } from '../../hooks';
import { Button } from '../button';
import { useFormField } from '../forms/hooks';
import { RadioFieldLayout } from '../forms/layouts/radio-field-layout.component';
import { useRadioContext } from '../forms/providers';
import { Text } from '../text';
import { ContentSwitcherStyles } from './content-switcher.styles';

type ContentSwitcherCtx = (nextValue: string) => void;

const [ContentSwitcherCtx, useContentSwitcher] = contextFactory<ContentSwitcherCtx>(
  'ContentSwitcher must wrap its Options'
);

type Props<T extends string> = {
  children: Array<React.ReactElement | null>;
  defaultValue: T;
  onValueChange: (nextValue: T) => void;
};

export const ContentSwitcher = <T extends string>({ children, defaultValue, onValueChange }: Props<T>) => {
  const contentSwitcherFieldProps = useFormField({
    type: 'optionswitch',
    value: defaultValue,
  });

  const handleValueChange = useCallback(
    (nextValue: string) => {
      onValueChange(nextValue as T);
    },
    [onValueChange]
  );

  return (
    <ContentSwitcherCtx.Provider value={handleValueChange}>
      <RadioFieldLayout {...contentSwitcherFieldProps} name='contentSwitcher'>
        <div css={ContentSwitcherStyles.container}>{children}</div>
      </RadioFieldLayout>
    </ContentSwitcherCtx.Provider>
  );
};

type ContentSwitchOptionProps<T extends string> = {
  value: T;
  iconName?: IconName;
  label?: string;
  disabled?: boolean;
  trackingId?: string;
  children?: React.ReactNode;
};

export const ContentSwitchOption = <T extends string>({
  value,
  iconName,
  label,
  trackingId,
  children,
}: ContentSwitchOptionProps<T>) => {
  const { getOptionProps } = useRadioContext();
  const onValueChange = useContentSwitcher();

  const { checked, name, onChange, ...optProps } = getOptionProps(value);

  const onClick = useCallback(() => {
    onChange({ name, value });
    onValueChange(value);
  }, [name, onChange, onValueChange, value]);

  return (
    <Button
      css={ContentSwitcherStyles.optionContainer(checked)}
      {...optProps}
      iconName={iconName}
      onClick={onClick}
      type='button'
      trackingId={trackingId}
    >
      {children ?? (
        <Text className='content-switcher-label' size='medium'>
          {label}
        </Text>
      )}
    </Button>
  );
};

ContentSwitcher.Option = ContentSwitchOption;
